/*
 *  Availability Facet
 *    Custom facet for availability filtering (in stock / out of stock)
 *
 *  Version:
 *    1.0.0 - 2024/01/01
 */

import { Facet } from '@process-creative/slate-ajax-collection';
import { t } from '@process-creative/slate-theme-tools';
import FilterUtils from '../../../utils/FilterUtils';
import { CLASS_HIDDEN } from './../../../tools/constants/classes';

// SELECTOR
const SELECTOR_CLEAR_FILTERS = '[data-collection-template-clear-filters]';

export class AvailabilityFacet extends Facet {
  constructor({ template, filter, title, container }) {
    super({ template, filter, title, container });
    this.containerElement = document.querySelector(container);
  }

  // Required method for facet interface
  onProductsDrawn() {
    this.containerElement.innerHTML = this.getPrint();
    this.bindEvents();
  }

  /**
   * Generate HTML for availability checkboxes (In Stock / Out of Stock)
   * Shows product counts and current selection state
   */
  getPrint() {
    const currentValues = this.filter.getValue() || [];
    const isActive = this.filter.isActive();
    
    // Get product counts for availability options
    const inStockCount = FilterUtils.getProductCountForAvailability(this.template.data.products || [], true);
    const outOfStockCount = FilterUtils.getProductCountForAvailability(this.template.data.products || [], false);
    
    // Check if values are selected
    const inStockSelected = currentValues.includes(true);
    const outOfStockSelected = currentValues.includes(false);
    
    let html = `
      <div class="c-collection-faceted-nav__facet-group" data-faceted-subnav>
        <ul class="c-facets__list" role="list">
    `;

    // In stock option
    html += `
      <li class="c-collection-faceted-nav__option o-heading--7" data-faceted-check-item>
        <input
          type="checkbox"
          class="c-facets__input is-checkbox c-collection-faceted-nav__option-input"
          id="availability-in-stock"
          data-availability-checkbox
          data-availability-value="in-stock"
          ${inStockSelected ? 'checked' : ''}
        />
        <label for="availability-in-stock" class="c-facets__checkbox c-collection-faceted-nav__option-label">
          <span class="c-facets__checkbox-text">${t('collections.collection.in_stock')}</span>
          <span class="c-facets__option-count">(${inStockCount})</span>
        </label>
      </li>
    `;

    // Out of stock option
    html += `
      <li class="c-collection-faceted-nav__option o-heading--7" data-faceted-check-item>
        <input
          type="checkbox"
          class="c-facets__input is-checkbox c-collection-faceted-nav__option-input"
          id="availability-out-of-stock"
          data-availability-checkbox
          data-availability-value="out-of-stock"
          ${outOfStockSelected ? 'checked' : ''}
        />
        <label for="availability-out-of-stock" class="c-facets__checkbox c-collection-faceted-nav__option-label">
          <span class="c-facets__checkbox-text">${t('collections.collection.out_of_stock')}</span>
          <span class="c-facets__option-count">(${outOfStockCount})</span>
        </label>
      </li>
    `;

    html += `
        </ul>
      </div>
    `;

    return html;
  }

  /**
   * Handle checkbox changes to update availability filter
   * Maps UI values ('in-stock', 'out-of-stock') to boolean filter values
   */
  bindEvents() {
    // Use event delegation on the container
    this.containerElement.addEventListener('change', (e) => {
      if (e.target.matches('[data-availability-checkbox]')) {
        const checkbox = e.target;
        const value = checkbox.dataset.availabilityValue;
        
        // Get current availability values
        const currentValues = this.filter.getValue() || [];
        let newValues = [...currentValues];
        
        if (checkbox.checked) {
          // Add the value if it's not already included
          if (value === 'in-stock' && !newValues.includes(true)) {
            newValues.push(true);
          } else if (value === 'out-of-stock' && !newValues.includes(false)) {
            newValues.push(false);
          }
        } else {
          // Remove the value
          if (value === 'in-stock') {
            newValues = newValues.filter(v => v !== true);
          } else if (value === 'out-of-stock') {
            newValues = newValues.filter(v => v !== false);
          }
        }
        
        // Update the filter with new values
        this.filter.setAvailability(newValues);
      }
    });
  }
}
